import { createGRPCAction, RequestActionType } from '@fullstory/redux-util';
import { defaultCacheKey } from '@fullstory/requests';
import { DefinedEvent, DefinedEventMetadata } from '@fullstory/proto/fs/services/search-settings';
import {
  DefinedEventsPublic,
  SetDefinedEventRequest,
} from '@fullstory/proto/fs/services/dx-objects';
import { CanAccessRequest } from '@fullstory/proto/fs/shared/accesscontrol';
import { DefinedEventsRequestTypes } from './types';
import { timestamp } from '@fullstory/proto/google/protobuf';

const getAllDefinedEventsCacheKeys = (orgId: string) => [
  defaultCacheKey(DefinedEventsPublic.GetAllDefinedEventsEndpoint, {
    orgId,
    includeArchived: true,
  }),
  defaultCacheKey(DefinedEventsPublic.GetAllDefinedEventsEndpoint, {
    orgId,
    includeArchived: false,
  }),
];

export const DefinedEventsRequestActions = {
  setDefinedEvent: (
    orgId: string,
    de: DefinedEvent,
    deMetadata: DefinedEventMetadata | null,
    idempotenceKey: string = '',
  ) => {
    const body: SetDefinedEventRequest = {
      orgId,
      definedEvent: de,
      definedEventMetadata: deMetadata,
      idempotenceKey,
    };
    return createGRPCAction<typeof DefinedEventsPublic.SetDefinedEvent>()(
      {
        type: DefinedEventsRequestTypes.SetDefinedEventRequest,
        grpcFunction: DefinedEventsPublic.SetDefinedEvent,
        body,
        invalidateCacheKey: getAllDefinedEventsCacheKeys(orgId),
        force: true,
        readWrite: 'write',
      },
      DefinedEventsRequestTypes.SetDefinedEventSuccess,
      DefinedEventsRequestTypes.SetDefinedEventFailure,
    );
  },
  getAllDefinedEventsRequest: (orgId: string, force?: boolean) =>
    createGRPCAction<typeof DefinedEventsPublic.GetAllDefinedEvents>()(
      {
        type: DefinedEventsRequestTypes.GetAllDefinedEventsRequest,
        grpcFunction: DefinedEventsPublic.GetAllDefinedEvents,
        body: { orgId, includeArchived: true },
        force,
        readWrite: 'read',
      },
      DefinedEventsRequestTypes.GetAllDefinedEventsSuccess,
      DefinedEventsRequestTypes.GetAllDefinedEventsFailure,
    ),
  archiveDefinedEventRequest: (orgId: string, id: string, archived: boolean) =>
    createGRPCAction<typeof DefinedEventsPublic.ArchiveDefinedEvent>()(
      {
        type: DefinedEventsRequestTypes.ArchiveDefinedEventRequest,
        grpcFunction: DefinedEventsPublic.ArchiveDefinedEvent,
        body: {
          orgId,
          definedEventId: id,
          archived,
        },
        invalidateCacheKey: getAllDefinedEventsCacheKeys(orgId),
        force: true,
        readWrite: 'write',
      },
      DefinedEventsRequestTypes.ArchiveDefinedEventSuccess,
      DefinedEventsRequestTypes.ArchiveDefinedEventFailure,
    ),
  archiveMultiDefinedEvents: (orgId: string, ids: string[], archived: boolean) =>
    createGRPCAction<typeof DefinedEventsPublic.ArchiveMultiDefinedEvents>()(
      {
        type: DefinedEventsRequestTypes.ArchiveMultiDefinedEventsRequest,
        grpcFunction: DefinedEventsPublic.ArchiveMultiDefinedEvents,
        body: {
          orgId,
          definedEventIds: ids,
          archived,
        },
        invalidateCacheKey: getAllDefinedEventsCacheKeys(orgId),
        force: true,
        readWrite: 'write',
      },
      DefinedEventsRequestTypes.ArchiveMultiDefinedEventsSuccess,
      DefinedEventsRequestTypes.ArchiveMultiDefinedEventsFailure,
    ),
  getAccess: (body: CanAccessRequest) =>
    createGRPCAction<typeof DefinedEventsPublic.GetDefinedEventAccess>()(
      {
        type: DefinedEventsRequestTypes.DefinedEventAccessRequest,
        grpcFunction: DefinedEventsPublic.GetDefinedEventAccess,
        body,
        readWrite: 'read',
      },
      DefinedEventsRequestTypes.DefinedEventAccessSuccess,
      DefinedEventsRequestTypes.DefinedEventAccessFailure,
    ),
  snoozeDefinedEvent: ({
    orgId,
    definedEventId,
    expiresAt,
  }: {
    orgId: string;
    definedEventId: string;
    expiresAt: timestamp | null;
  }) =>
    createGRPCAction<typeof DefinedEventsPublic.SilenceIssue>()(
      {
        type: DefinedEventsRequestTypes.SnoozeDefinedEventRequest,
        grpcFunction: DefinedEventsPublic.SilenceIssue,
        body: {
          orgId,
          definedEventId,
          expiresAt,
        },
        force: true,
        readWrite: 'write',
      },
      DefinedEventsRequestTypes.SnoozeDefinedEventSuccess,
      DefinedEventsRequestTypes.SnoozeDefinedEventFailure,
    ),
  unsnoozeDefinedEvent: ({
    orgId,
    definedEventId,
    issueSilenceId,
  }: {
    orgId: string;
    definedEventId: string;
    issueSilenceId: string;
  }) =>
    createGRPCAction<typeof DefinedEventsPublic.UnSilenceIssue>()(
      {
        type: DefinedEventsRequestTypes.UnSnoozeDefinedEventRequest,
        grpcFunction: DefinedEventsPublic.UnSilenceIssue,
        body: {
          orgId,
          definedEventId,
          issueSilenceId,
        },
        force: true,
        readWrite: 'write',
      },
      DefinedEventsRequestTypes.UnSnoozeDefinedEventSuccess,
      DefinedEventsRequestTypes.UnSnoozeDefinedEventFailure,
    ),
};

export type DefinedEventsActions = RequestActionType<typeof DefinedEventsRequestActions>;
